import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppNavLink, useAppNavigate } from "../lib/route-primitives";
import {
  User, Mail, Phone, Lock, Star, LifeBuoy,
  AlertTriangle, CheckCircle2, XCircle,
  Calendar, Loader2, ArrowRight, Pencil, X,
} from "lucide-react";
import { organizerProfileApi, organizerSupportApi } from "../lib/api";
import { PLAN_LABELS, PLAN_LIMITS, type PlanId } from "@wedding/shared";
import { OrganizerPageHeader } from "../components/organizer-page-header";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { AccountPackages } from "../components/account-packages";

// ─── Toast ────────────────────────────────────────────────────────────
function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
        "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
        ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
      )}
    >
      {ok
        ? <CheckCircle2 className="h-4 w-4 shrink-0" />
        : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

// ─── Plan config ──────────────────────────────────────────────────────
const isStdPlan = (slug: string): slug is PlanId => Object.prototype.hasOwnProperty.call(PLAN_LIMITS, slug);

const PLAN_STYLES: Record<PlanId, { badge: string; glow: string; ring: string }> = {
  free: {
    badge: "bg-muted text-muted-foreground border border-border",
    glow: "from-muted/30 to-muted/10",
    ring: "ring-border",
  },
  pro: {
    badge: "bg-primary/10 text-primary border border-primary/20",
    glow: "from-primary/20 to-primary/5",
    ring: "ring-primary/30",
  },
  max: {
    badge: "bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/40",
    glow: "from-amber-500/20 to-amber-500/5",
    ring: "ring-amber-400/30",
  },
};

// ─── Phone change modal ───────────────────────────────────────────────
const E164_RE = /^\+[1-9]\d{7,14}$/;

function PhoneChangeModal({
  currentPhone,
  email,
  onClose,
}: {
  currentPhone: string | null;
  email: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [newPhone, setNewPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, isSubmitting]);

  const validate = () => {
    if (!newPhone.trim()) { setValidationError(t("profile.phoneChangeRequired")); return false; }
    if (!E164_RE.test(newPhone.trim())) { setValidationError(t("profile.phoneChangeFormat")); return false; }
    if (currentPhone && newPhone.trim() === currentPhone) { setValidationError(t("profile.phoneChangeSame")); return false; }
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const body = [
        `${t("profile.phoneLabel")} (${t("profile.phoneCurrent")}): ${currentPhone ?? t("profile.phoneNone")}`,
        `${t("profile.phoneChangeNew")}: ${newPhone.trim()}`,
        `${t("profile.emailLabel")}: ${email}`,
        "",
        t("profile.phoneChangeAutoNote"),
      ].join("\n");

      const res = await organizerSupportApi.create({
        subject: t("profile.phoneChangeSubject"),
        body,
        category: "general",
        priority: "normal",
      });
      if (res.ok && res.data?.id) setTicketId(res.data.id);
    } catch {
      setValidationError(t("profile.phoneChangeFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => { if (!isSubmitting) onClose(); }}
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm rounded-2xl bg-background border border-border shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">{t("profile.phoneChangeTitle")}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-40 p-1 rounded-lg hover:bg-muted"
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Success state */}
        {ticketId ? (
          <div className="p-5 space-y-4">
            <div className="flex flex-col items-center gap-3 py-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-sm">{t("profile.phoneChangeSuccess")}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t("profile.phoneChangeSuccessDesc")}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <AppNavLink
                to={paths.supportDetail(ticketId)}
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary hover:bg-primary/90 px-3 py-2.5 text-xs font-semibold text-white transition-colors cursor-pointer"
              >
                <LifeBuoy className="h-3.5 w-3.5" />
                {t("profile.phoneChangeViewTicket")}
              </AppNavLink>
              <button
                onClick={onClose}
                className="flex-1 rounded-xl border border-border px-3 py-2.5 text-xs font-medium hover:bg-muted transition-colors cursor-pointer"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        ) : (
          /* Request form */
          <div className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("profile.phoneChangeInfo")}
            </p>

            {/* Current → New */}
            <div className="space-y-2">
              {/* Current */}
              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t("profile.phoneCurrent")}
                </p>
                <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-sm">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="font-mono text-muted-foreground">
                    {currentPhone ?? t("profile.phoneNone")}
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 rotate-90" />
              </div>

              {/* New */}
              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t("profile.phoneChangeNew")}
                </p>
                <input
                  ref={inputRef}
                  type="tel"
                  value={newPhone}
                  onChange={(e) => { setNewPhone(e.target.value); setValidationError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  placeholder="+905551234567"
                  className={cn(
                    "w-full rounded-xl border bg-background px-3.5 py-2.5 text-sm font-mono",
                    "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all",
                    "placeholder:text-muted-foreground/40",
                    validationError ? "border-red-400" : "border-border",
                  )}
                />
                {validationError && (
                  <p className="mt-1.5 text-[11px] text-red-500 flex items-center gap-1">
                    <XCircle className="h-3 w-3 shrink-0" />
                    {validationError}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !newPhone.trim()}
                className={cn(
                  "flex-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white",
                  "hover:bg-primary/90 transition-colors cursor-pointer",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                  "flex items-center justify-center gap-2",
                )}
              >
                {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t("profile.phoneChangeSubmit")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Deactivate Modal ─────────────────────────────────────────────────
function DeactivateModal({
  onConfirm, onCancel, isLoading,
}: { onConfirm: () => void; onCancel: () => void; isLoading: boolean }) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !isLoading) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, isLoading]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!isLoading) onCancel(); }} />
      <div className="relative w-full max-w-sm rounded-2xl bg-background border border-border shadow-2xl p-6 space-y-4 animate-in zoom-in-95 fade-in duration-200">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mx-auto">
          <AlertTriangle className="h-6 w-6 text-red-500" />
        </div>
        <div className="text-center space-y-1.5">
          <h3 className="text-base font-bold">{t("profile.deactivateTitle")}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{t("profile.deactivateDesc")}</p>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("profile.deactivateConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Phone Verify Modal (SMS test için mevcut numarayı doğrula) ───────
function PhoneVerifyModal({ phone, onClose, onVerified }: {
  phone: string; onClose: () => void; onVerified: () => void;
}) {
  const { t } = useTranslation();
  const [verificationSid, setVerificationSid] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sendCode = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await organizerProfileApi.requestPhoneVerify();
      if (!res.ok) throw new Error(res.error || t("common.error"));
      setVerificationSid((res.data as { verificationSid: string | null })?.verificationSid ?? null);
    } catch (e) { setError(e instanceof Error ? e.message : t("common.error")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { sendCode(); }, [sendCode]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !loading) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, loading]);

  const verify = async () => {
    if (code.length !== 6 || loading) return;
    setLoading(true); setError("");
    try {
      const res = await organizerProfileApi.verifyPhone({ code, verificationSid: verificationSid ?? undefined });
      if (!res.ok) throw new Error(res.error || t("common.error"));
      onVerified();
    } catch (e) { setError(e instanceof Error ? e.message : t("common.error")); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!loading) onClose(); }} />
      <div className="relative w-full max-w-sm rounded-2xl bg-background border border-border shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">{t("profile.phoneVerifyTitle")}</h3>
          </div>
          <button onClick={onClose} disabled={loading} className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-40 p-1 rounded-lg hover:bg-muted" aria-label={t("common.close")}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">{t("profile.phoneVerifyInfo", { phone })}</p>
          <input
            type="text" inputMode="numeric" maxLength={6} autoFocus
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
            placeholder="000000"
            className={cn(
              "w-full rounded-xl border bg-background px-3.5 py-2.5 text-center text-lg font-mono tracking-[0.3em]",
              "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all",
              error ? "border-red-400" : "border-border",
            )}
          />
          {error && <p className="text-[11px] text-red-500 flex items-center gap-1"><XCircle className="h-3 w-3 shrink-0" />{error}</p>}
          <div className="flex gap-2">
            <button onClick={sendCode} disabled={loading}
              className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50">
              {t("profile.phoneVerifyResend")}
            </button>
            <button onClick={verify} disabled={loading || code.length !== 6}
              className="flex-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t("profile.phoneVerifySubmit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────
export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const [profile, setProfile] = useState<{
    id: string;
    email: string;
    display_name: string | null;
    phone_e164: string | null;
    phone_verified_at: string | null;
    plan: string;
    created_at: string;
  } | null>(null);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [planInfo, setPlanInfo] = useState<{ label: string; maxWeddings: number; accountWide?: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showPhonePanel, setShowPhonePanel] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    organizerProfileApi
      .get()
      .then((res) => {
        if (res.ok && res.data) {
          const org = res.data.organizer as typeof profile;
          setProfile(org);
          setDisplayName(org?.display_name ?? "");
          setPlanInfo(res.data.planInfo ?? null);
        }
      })
      .catch(() => showToast(t("profile.loadFailed"), false))
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await organizerProfileApi.update({ displayName: displayName || undefined });
      setProfile((p) => p ? { ...p, display_name: displayName } : p);
      showToast(t("profile.updateSuccess"));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("profile.updateFailed"), false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      await organizerProfileApi.deactivate();
      showToast(t("profile.deactivateSuccess"));
      setShowDeactivateModal(false);
      // Organizatör panelinde `/login` diye bir rota YOK — login formu `/`'da
      // render edilir (AuthGate). Eskiden `navigate("/login")` yazıyordu; iki
      // gereksiz redirect hop'u üzerinden tesadüfen çalışıyordu.
      setTimeout(() => navigate(paths.root), 1800);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("profile.deactivateFailed"), false);
      setIsDeactivating(false);
      setShowDeactivateModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <OrganizerPageHeader />
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Admin özel plan atayabilir (ör. `enterprise`) — sabitlerde karşılığı yok.
  // Ad + limit sunucudan (DB); sabitler yalnız eski worker için yedek.
  const planSlug = profile?.plan ?? "free";
  const stdPlan = isStdPlan(planSlug) ? planSlug : null;
  const planLabel = planInfo?.label ?? (stdPlan ? PLAN_LABELS[stdPlan] : planSlug);
  // Paket düğündedir (2026-10-15): profil rozeti yalnız hesaba özel ("hesap geneli") planda.
  // Eski worker `accountWide` göndermez → standart olmayan ya da Free dışı plan hesap geneli sayılır.
  const accountWide = planInfo?.accountWide ?? planSlug !== "free";
  const planStyle = accountWide ? PLAN_STYLES[stdPlan ?? "pro"] : PLAN_STYLES.free;

  const initials = (profile?.display_name ?? profile?.email ?? "?")
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(i18n.language, { year: "numeric", month: "long" })
    : null;

  const nameChanged = displayName !== (profile?.display_name ?? "");

  return (
    <div className="min-h-screen bg-background">
      <OrganizerPageHeader />

      <main className="mx-auto max-w-7xl px-4 md:px-8 py-8 pb-20 md:pb-8">

        {/* ── Profile Hero ────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-8">
          <div className={cn(
            "relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full",
            "bg-gradient-to-br from-primary to-primary/70",
            "text-white text-2xl font-bold shadow-lg shadow-primary/30 ring-4 ring-background",
          )}>
            {initials}
            <div className={cn("absolute inset-0 rounded-full ring-2", planStyle.ring)} />
          </div>

          <div className="text-center sm:text-left space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {profile?.display_name ?? profile?.email?.split("@")[0]}
            </h1>
            <p className="text-sm text-muted-foreground flex items-center justify-center sm:justify-start gap-1.5">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              {profile?.email}
            </p>
            <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap pt-0.5">
              {accountWide && (
                <span
                  data-testid="profile-account-wide"
                  className={cn("inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold", planStyle.badge)}
                >
                  <Star className="h-3 w-3" />
                  <span>{t("profile.accountWideBadge", { plan: planLabel })}</span>
                </span>
              )}
              {memberSince && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {t("profile.memberSince", { date: memberSince })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Grid ────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* ── Account Card ─── */}
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                {t("profile.accountInfo")}
              </h2>
            </div>

            <div className="p-5 space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("profile.emailLabel")}
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  {profile?.email}
                </div>
              </div>

              {/* Display name */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("profile.nameLabel")}
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && nameChanged) handleSave(); }}
                  className={cn(
                    "w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm",
                    "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all",
                    "placeholder:text-muted-foreground/50",
                  )}
                  placeholder={t("profile.nameLabel")}
                />
              </div>

              {/* Phone section */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Lock className="h-3 w-3" />
                    {t("profile.phoneLabel")}
                  </label>

                  {/* Phone row: value + change button */}
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-sm min-w-0">
                      <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className={cn("truncate font-mono", profile?.phone_e164 ? "text-foreground" : "text-muted-foreground")}>
                        {profile?.phone_e164 ?? "—"}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowPhonePanel(true)}
                      className={cn(
                        "shrink-0 flex items-center gap-1.5 rounded-xl border border-border",
                        "px-3 py-2.5 text-xs font-medium text-muted-foreground",
                        "hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-all cursor-pointer",
                      )}
                      title={t("profile.phoneChangeTitle")}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{t("profile.phoneChangeAction")}</span>
                    </button>
                  </div>

                  {/* SMS test için doğrulama durumu */}
                  {profile?.phone_e164 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {profile.phone_verified_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {t("profile.phoneVerified")}
                        </span>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                            <AlertTriangle className="h-3.5 w-3.5" /> {t("profile.phoneNotVerified")}
                          </span>
                          <button
                            onClick={() => setShowPhoneVerify(true)}
                            className="text-xs px-2.5 py-1 rounded-lg border border-primary/40 text-primary hover:bg-primary/5 transition-colors cursor-pointer"
                          >
                            {t("profile.phoneVerifyAction")}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

              </div>

              {/* Save — only active when name changed */}
              <button
                onClick={handleSave}
                disabled={isSaving || !nameChanged}
                className={cn(
                  "w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white",
                  "hover:bg-primary/90 active:scale-[0.98] transition-all cursor-pointer",
                  "disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2",
                )}
              >
                {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isSaving ? t("common.saving") : t("common.save")}
              </button>
            </div>
          </div>

          {/* ── Right column ─── */}
          <div className="space-y-5">
            {/* Plan Card */}
            <div className={cn(
              "rounded-2xl border border-border bg-gradient-to-br overflow-hidden shadow-sm",
              planStyle.glow,
            )}>
              <div className="px-5 py-4 border-b border-border/50">
                <h2 className="font-semibold text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  {t("profile.accountPackages")}
                </h2>
              </div>

              <AccountPackages accountLabel={planLabel} accountWide={accountWide} />
            </div>

            {/* Danger Zone */}
            <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-card overflow-hidden shadow-sm">
              <div className="px-5 py-4 border-b border-red-200/60 dark:border-red-900/30">
                <h2 className="font-semibold text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {t("profile.dangerZone")}
                </h2>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">{t("profile.deactivateTitle")}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
                      {t("profile.deactivateDesc")}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeactivateModal(true)}
                    className={cn(
                      "shrink-0 rounded-xl border border-red-300 dark:border-red-800/60",
                      "px-3.5 py-2 text-xs font-semibold text-red-600 dark:text-red-400",
                      "hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer",
                    )}
                  >
                    {t("profile.deactivateTitle")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showDeactivateModal && (
        <DeactivateModal
          onConfirm={handleDeactivate}
          onCancel={() => setShowDeactivateModal(false)}
          isLoading={isDeactivating}
        />
      )}

      {showPhonePanel && (
        <PhoneChangeModal
          currentPhone={profile?.phone_e164 ?? null}
          email={profile?.email ?? ""}
          onClose={() => setShowPhonePanel(false)}
        />
      )}

      {showPhoneVerify && profile?.phone_e164 && (
        <PhoneVerifyModal
          phone={profile.phone_e164}
          onClose={() => setShowPhoneVerify(false)}
          onVerified={() => {
            setProfile((p) => (p ? { ...p, phone_verified_at: new Date().toISOString() } : p));
            setShowPhoneVerify(false);
            showToast(t("profile.phoneVerifySuccess"));
          }}
        />
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={dismissToast} />}
    </div>
  );
}
